import fields from './fields';
import { CustomPricingType } from './pricingType';

/**
 * Pricing plans the buyer picks on the store checkout (the pos-brokers-checkout
 * WordPress plugin). The store sends the plan id as application.plan and a
 * snapshot of the card as storeOrders/{id}.order.pricingPlan.
 *
 * The CRM values below come from John's package screenshots (28.09.2026). They
 * fill the Pricing step once, the rep can still change every field.
 */

type StorePlanValue = { category: string; name: string; value: string };

type StorePlan = {
  label: string;
  pricingType: CustomPricingType | null;
  values: StorePlanValue[];
};

const ofThreeTierCategories = (name: string, value: string, amexName = name): StorePlanValue[] => [
  { category: '3-Tier Credit Card Discount Fees', name, value },
  { category: '3-Tier Offline-Debit Discount Fees', name, value },
  { category: 'Amex 3-Tier Rates', name: amexName, value },
];

export const STORE_PRICING_PLANS: Record<string, StorePlan> = {
  cash_discount: {
    label: 'Cash Discount',
    pricingType: CustomPricingType.Flat,
    values: [
      // The buyer's customer pays 4%, which is 4/104 = 3.846% of the total.
      { category: '3-Tier Credit Card Discount Fees', name: 'Credit Card Discount Rate', value: '3.846' },
      { category: '3-Tier Offline-Debit Discount Fees', name: 'Offline-Debit Discount Rate', value: '3.846' },
      { category: 'Amex 3-Tier Rates', name: 'Discount Rate', value: '3.846' },
    ],
  },
  standard: {
    label: 'Standard Pricing',
    pricingType: CustomPricingType.ThreeTier,
    values: [
      ...ofThreeTierCategories('Qualified Fee', '1.69', 'Qualified Discount Fee'),
      ...ofThreeTierCategories('Mid-Qualified Fee: Qual+', '0.76'),
      ...ofThreeTierCategories('Non-Qualified Fee: Qual+', '3.16'),
      { category: 'Processing Fees', name: 'Visa/MC/Disc Tran Fee', value: '0.20' },
    ],
  },
  consult: {
    label: 'Not Ready to Select (talk to a representative)',
    pricingType: null,
    values: [],
  },
};

const fieldsByType: Record<CustomPricingType, any[]> = {
  [CustomPricingType.Flat]: fields.flatFields,
  [CustomPricingType.ThreeTier]: fields.tierFields,
  [CustomPricingType.ICPlus]: fields.icFields,
};

/**
 * Resolves the plan values to Pricing step input names. A value that does not
 * match a field is a typo in the table above, so it fails loudly.
 */
const inputValues = (plan: StorePlan): Record<string, string> => {
  if (!plan.pricingType) {
    return {};
  }
  const available = fieldsByType[plan.pricingType];
  return plan.values.reduce((result, { category, name, value }) => {
    const field = available.find(f => f.category === category && f.name === name);
    if (!field) {
      throw new Error(`Unknown ${plan.pricingType} pricing field "${category} / ${name}"`);
    }
    result[field.inputName] = value;
    return result;
  }, {} as Record<string, string>);
};

export const storePricingPlanFor = (application: any): StorePlan | null =>
  application?.storeId && typeof application.plan === 'string'
    ? STORE_PRICING_PLANS[application.plan] || null
    : null;

/**
 * Pre-fills the Pricing step from the store plan. It runs when the rep opens
 * the step for the first time, and again when the rep switches the pricing
 * type back to the plan's type (switching clears customPricing). Values the
 * rep already typed are kept.
 */
export const applyStorePricingPlan = (application: any): boolean => {
  const plan = storePricingPlanFor(application);
  if (!plan || !plan.pricingType) {
    return false;
  }

  const firstVisit = !application.customPricingType && !application.customPricing;
  const switchedBack =
    application.customPricingType === plan.pricingType &&
    application.customPricing &&
    Object.keys(application.customPricing).length === 0;
  if (!firstVisit && !switchedBack) {
    return false;
  }

  application.customPricingType = plan.pricingType;
  application.customPricing = { ...inputValues(plan), ...(application.customPricing || {}) };
  return true;
};

/**
 * What the buyer saw on the store card, from the order snapshot. Falls back to
 * the plan id when the order has no snapshot.
 */
export const storePricingPlanSummary = (application: any, snapshot: any) => {
  const plan = storePricingPlanFor(application);
  const name = typeof snapshot?.name === 'string' && snapshot.name.trim()
    ? snapshot.name.trim()
    : plan?.label || '';
  const summary = typeof snapshot?.summary === 'string' ? snapshot.summary.trim() : '';
  if (!name) {
    return null;
  }
  return { name, summary, prefilled: !!plan?.pricingType, pricingType: plan?.pricingType || null };
};
